import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
    Paper,
    Button,
    Grid,
    Divider,
    alpha,
    useTheme,
    Avatar
} from '@mui/material';
import {
    EmojiEvents as TrophyIcon,
    Timer as TimerIcon,
    CheckCircle as CheckIcon,
    Home as HomeIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, useSpring, useTransform } from 'framer-motion';
import confetti from 'canvas-confetti';
import StudentLayout from 'components/layout/StudentLayout';
import LeaderboardTable from 'components/quiz/LeaderboardTable';
import { getMyAttemptWithResponses, getQuizLeaderboard, getQuiz } from 'services/quizService';
import { AttemptWithResponses, QuizLeaderboardEntry, QuizWithQuestions } from 'types/quiz';

// Animated counter component
const AnimatedCounter: React.FC<{ value: number; duration?: number }> = ({ value, duration = 1500 }) => {
    const spring = useSpring(0, { duration });
    const display = useTransform(spring, (current) => Math.round(current));
    const [displayValue, setDisplayValue] = useState(0);

    useEffect(() => {
        spring.set(value);
        const unsubscribe = display.on('change', (latest) => {
            setDisplayValue(latest);
        });
        return () => unsubscribe();
    }, [value, spring, display]);

    return <>{displayValue}</>;
};

function QuizResultsPageV2() {
    const theme = useTheme();
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const quizId = Number(id);

    const [attemptData, setAttemptData] = useState<AttemptWithResponses | null>(null);
    const [quiz, setQuiz] = useState<QuizWithQuestions | null>(null);
    const [leaderboard, setLeaderboard] = useState<QuizLeaderboardEntry[]>([]);
    const [userRank, setUserRank] = useState<number>(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const hasNavigatedRef = React.useRef(false);

    const triggerCelebration = (rank: number) => {
        if (rank === 1) {
            const duration = 3000;
            const end = Date.now() + duration;

            const frame = () => {
                confetti({
                    particleCount: 5,
                    angle: 60,
                    spread: 55,
                    origin: { x: 0, y: 0.6 },
                    colors: ['#FFD700', '#FFA500', '#FF8C00'],
                });
                confetti({
                    particleCount: 5,
                    angle: 120,
                    spread: 55,
                    origin: { x: 1, y: 0.6 },
                    colors: ['#FFD700', '#FFA500', '#FF8C00'],
                });

                if (Date.now() < end) {
                    requestAnimationFrame(frame);
                }
            };
            frame();
        } else if (rank === 2) {
            confetti({
                particleCount: 100,
                spread: 70,
                origin: { y: 0.6 },
                colors: ['#C0C0C0', '#A9A9A9', '#D3D3D3'],
            });
        } else if (rank === 3) {
            confetti({
                particleCount: 100,
                spread: 70,
                origin: { y: 0.6 },
                colors: ['#CD7F32', '#B87333', '#966F33'],
            });
        }
    };

    const loadResults = useCallback(async () => {
        try {
            setLoading(true);

            const [attemptResult, quizData, leaderboardData] = await Promise.all([
                getMyAttemptWithResponses(quizId),
                getQuiz(quizId),
                getQuizLeaderboard(quizId),
            ]);

            if (!attemptResult || !attemptResult.completedAt) {
                if (!hasNavigatedRef.current) {
                    hasNavigatedRef.current = true;
                    navigate(`/student/v2/quizzes/${quizId}/play`);
                }
                return;
            }

            setAttemptData(attemptResult);
            setQuiz(quizData);
            setLeaderboard(leaderboardData);

            const userEntry = leaderboardData.find((entry) => entry.userId === attemptResult.userId);
            const actualRank = userEntry ? userEntry.rank : 0;
            setUserRank(actualRank);

            setLoading(false);

            if (actualRank <= 3) {
                setTimeout(() => triggerCelebration(actualRank), 500);
            }
        } catch (err: unknown) {
            console.error('Error loading results:', err);
            const error = err as { response?: { data?: { error?: string } } };
            setError(error.response?.data?.error || 'Failed to load results');
            setLoading(false);
        }
    }, [quizId, navigate]);

    useEffect(() => {
        loadResults();
    }, [loadResults]);

    const formatTime = (seconds: number): string => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const getTimeTakenSeconds = (): number => {
        if (!attemptData || !attemptData.startedAt || !attemptData.completedAt) return 0;
        const start = new Date(attemptData.startedAt).getTime();
        const end = new Date(attemptData.completedAt).getTime();
        return Math.floor((end - start) / 1000);
    };

    const getCorrectAnswersCount = (): number => {
        if (!attemptData) return 0;
        return attemptData.responses.filter((response) => response.isCorrect).length;
    };

    const getPercentage = (): number => {
        if (!attemptData) return 0;
        const correct = getCorrectAnswersCount();
        return Math.round((correct / attemptData.totalQuestions) * 100);
    };

    const getRankMessage = (): string => {
        if (userRank === 0) return 'Ranking not available';
        if (userRank === 1) return '🥇 You placed 1st! Amazing!';
        if (userRank === 2) return '🥈 You placed 2nd! Great job!';
        if (userRank === 3) return '🥉 You placed 3rd! Well done!';
        return `You placed #${userRank}`;
    };

    if (loading) {
        return (
            <StudentLayout>
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            </StudentLayout>
        );
    }

    if (error || !attemptData || !quiz) {
        return (
            <StudentLayout>
                <Box sx={{ maxWidth: 800, mx: 'auto' }}>
                    <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>
                        {error || 'Failed to load results'}
                    </Alert>
                    <Button
                        variant="contained"
                        onClick={() => navigate('/student/v2/quizzes')}
                        sx={{ borderRadius: 2 }}
                    >
                        Back to Quizzes
                    </Button>
                </Box>
            </StudentLayout>
        );
    }

    return (
        <StudentLayout>
            <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
                {/* Header */}
                <Box
                    sx={{
                        textAlign: 'center',
                        mb: 4,
                        p: 4,
                        borderRadius: 4,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`,
                        border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
                    }}
                >
                    <Typography variant="h3" fontWeight="800" gutterBottom sx={{ color: theme.palette.success.dark }}>
                        Quiz Complete!
                    </Typography>
                    <Typography variant="h6" color="text.secondary">
                        {quiz.title}
                    </Typography>
                </Box>

                {/* Score Display */}
                <motion.div
                    initial={{ scale: 0.5, opacity: 0, y: 50 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    transition={{ type: 'spring', stiffness: 100, damping: 15 }}
                >
                    <Paper
                        elevation={0}
                        sx={{
                            p: 4,
                            mb: 4,
                            textAlign: 'center',
                            borderRadius: 4,
                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                            color: 'white',
                            border: `2px solid ${alpha(theme.palette.primary.dark, 0.3)}`
                        }}
                    >
                        <motion.div
                            animate={{ rotate: [0, -10, 10, -10, 0], scale: [1, 1.1, 1] }}
                            transition={{ duration: 0.6, delay: 0.3 }}
                        >
                            <TrophyIcon sx={{ fontSize: 80, color: '#FFD700', mb: 2 }} />
                        </motion.div>
                        <Typography variant="h2" fontWeight="800" gutterBottom sx={{ color: 'white' }}>
                            <AnimatedCounter value={attemptData.score} /> Points
                        </Typography>
                        <Typography variant="h5" gutterBottom sx={{ color: 'rgba(255,255,255,0.95)' }}>
                            <AnimatedCounter value={getCorrectAnswersCount()} /> / {attemptData.totalQuestions} Correct ({getPercentage()}%)
                        </Typography>
                        <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)', mt: 2 }}>
                            {getRankMessage()}
                        </Typography>
                    </Paper>
                </motion.div>

                {/* Stats Grid */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={4}>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3 }}
                        >
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 3,
                                    textAlign: 'center',
                                    borderRadius: 3,
                                    border: `1px solid ${theme.palette.divider}`,
                                    bgcolor: 'background.paper',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: `0 8px 24px ${alpha(theme.palette.success.main, 0.15)}`
                                    }
                                }}
                            >
                                <Avatar
                                    sx={{
                                        bgcolor: alpha(theme.palette.success.main, 0.15),
                                        color: theme.palette.success.main,
                                        width: 56,
                                        height: 56,
                                        mx: 'auto',
                                        mb: 2
                                    }}
                                >
                                    <CheckIcon sx={{ fontSize: 32 }} />
                                </Avatar>
                                <Typography variant="h4" fontWeight="700">
                                    <AnimatedCounter value={getCorrectAnswersCount()} duration={1000} />
                                </Typography>
                                <Typography variant="body1" color="text.secondary" fontWeight="600">
                                    Correct Answers
                                </Typography>
                            </Paper>
                        </motion.div>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.5 }}
                        >
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 3,
                                    textAlign: 'center',
                                    borderRadius: 3,
                                    border: `1px solid ${theme.palette.divider}`,
                                    bgcolor: 'background.paper',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: `0 8px 24px ${alpha(theme.palette.info.main, 0.15)}`
                                    }
                                }}
                            >
                                <Avatar
                                    sx={{
                                        bgcolor: alpha(theme.palette.info.main, 0.15),
                                        color: theme.palette.info.main,
                                        width: 56,
                                        height: 56,
                                        mx: 'auto',
                                        mb: 2
                                    }}
                                >
                                    <TimerIcon sx={{ fontSize: 32 }} />
                                </Avatar>
                                <Typography variant="h4" fontWeight="700">
                                    {formatTime(getTimeTakenSeconds())}
                                </Typography>
                                <Typography variant="body1" color="text.secondary" fontWeight="600">
                                    Time Taken
                                </Typography>
                            </Paper>
                        </motion.div>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.7 }}
                        >
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 3,
                                    textAlign: 'center',
                                    borderRadius: 3,
                                    border: `1px solid ${theme.palette.divider}`,
                                    bgcolor: 'background.paper',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: `0 8px 24px ${alpha(theme.palette.warning.main, 0.15)}`
                                    }
                                }}
                            >
                                <motion.div
                                    animate={
                                        userRank <= 3
                                            ? { rotate: [0, -15, 15, -15, 15, 0], scale: [1, 1.2, 1] }
                                            : {}
                                    }
                                    transition={{ duration: 0.8, delay: 1 }}
                                >
                                    <Avatar
                                        sx={{
                                            bgcolor: alpha(
                                                userRank === 1 ? '#FFD700' : userRank === 2 ? '#C0C0C0' : userRank === 3 ? '#CD7F32' : theme.palette.warning.main,
                                                0.15
                                            ),
                                            color: userRank === 1 ? '#FFD700' : userRank === 2 ? '#C0C0C0' : userRank === 3 ? '#CD7F32' : theme.palette.warning.main,
                                            width: 56,
                                            height: 56,
                                            mx: 'auto',
                                            mb: 2
                                        }}
                                    >
                                        <TrophyIcon sx={{ fontSize: 32 }} />
                                    </Avatar>
                                </motion.div>
                                <Typography variant="h4" fontWeight="700">
                                    #{userRank}
                                </Typography>
                                <Typography variant="body1" color="text.secondary" fontWeight="600">
                                    Your Rank
                                </Typography>
                            </Paper>
                        </motion.div>
                    </Grid>
                </Grid>

                <Divider sx={{ my: 4 }} />

                {/* Leaderboard */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h4" fontWeight="700" gutterBottom>
                        Leaderboard
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                        See how you rank against other participants
                    </Typography>
                    <LeaderboardTable entries={leaderboard} maxEntries={50} />
                </Box>

                {/* Actions */}
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                    <Button
                        variant="contained"
                        size="large"
                        startIcon={<HomeIcon />}
                        onClick={() => navigate('/student/v2/quizzes')}
                        sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 700,
                            px: 4,
                            py: 1.5
                        }}
                    >
                        Back to Quizzes
                    </Button>
                </Box>
            </Box>
        </StudentLayout>
    );
}

export default QuizResultsPageV2;
